import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Store, Loader2, UserCheck, ShoppingBag } from "lucide-react";
import { api, errMsg, orderKey } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function BuyChoice({ guest, setGuest, needsLogin }) {
  const { lang, config } = useApp();
  const nav = useNavigate();
  const id = lang === "id";
  const pct = config.member_discount_pct || 0;
  const login = () => { localStorage.setItem("ce_return", "/checkout"); nav("/login"); };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="buy-choice">
      <button onClick={login} data-testid="buy-choice-login" className="text-left border-2 border-[#3B220C] bg-[#F6F2EE] p-5 space-y-2 hover:-translate-y-0.5 transition-transform">
        <span className="flex items-center gap-2 font-medium"><UserCheck size={18} />{id ? "Masuk / Daftar akun" : "Sign in / Register"}</span>
        <span className="block text-sm text-[#8C4F27]">{id ? `Hemat ${pct}% member di setiap pesanan + kupon selamat datang` : `Save ${pct}% member discount on every order + welcome coupon`}</span>
      </button>
      <button disabled={needsLogin} onClick={() => setGuest(true)} data-testid="buy-choice-guest"
        className={`text-left border p-5 space-y-2 transition-colors disabled:opacity-40 ${guest ? "border-[#3B220C] bg-white" : "border-[#E6DFD5] bg-white"}`}>
        <span className="flex items-center gap-2 font-medium"><ShoppingBag size={18} />{id ? "Beli langsung (tamu)" : "Buy as guest"}</span>
        <span className="block text-sm text-[#6E6259]">{needsLogin ? (id ? "Produk digital, kursus & workshop wajib masuk akun" : "Digital, courses & workshops need an account") : (id ? "Tanpa akun, tanpa diskon member" : "No account, no member discount")}</span>
      </button>
    </div>
  );
}

const COUNTRIES = [["ID", "Indonesia"], ["SG", "Singapore"], ["MY", "Malaysia"], ["AU", "Australia"], ["JP", "Japan"], ["US", "United States"], ["GB", "United Kingdom"], ["NL", "Netherlands"]];
const inputCls = "w-full border border-[#E6DFD5] bg-white px-4 py-3 outline-none focus:border-[#8C4F27] transition-colors";
const Section = ({ title, children }) => (<section className="space-y-4"><h2 className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{title.toUpperCase()}</h2>{children}</section>);

export default function Checkout() {
  const { t, fmt, cart, user, config } = useApp();
  const nav = useNavigate();
  const [cust, setCust] = useState({ name: "", email: "", phone: "" });
  const [ship, setShip] = useState({ method: "courier", country: "ID", postal_code: "", address: "", city: "" });
  const [rates, setRates] = useState(null);
  const [rate, setRate] = useState(null);
  const [coupon, setCoupon] = useState(localStorage.getItem("ce_promo") || "");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState(false);
  const placed = useRef(false);
  const physical = cart.items.filter((i) => i.type === "physical");
  const needsLogin = !user && cart.items.some((i) => i.type !== "physical");

  useEffect(() => {
    if (!user) { return; }
    setCust((c) => ({ ...c, name: c.name || user.name, email: c.email || user.contact_email || user.email, phone: c.phone || user.phone || "" }));
    setShip((s) => ({ ...s, address: s.address || user.address || "", city: s.city || user.city || "", postal_code: s.postal_code || user.postal_code || "" }));
  }, [user]);
  useEffect(() => { setRates(null); setRate(null); }, [ship.country, ship.postal_code]);
  if (!cart.items.length && !placed.current) return <Navigate to="/cart" replace />;

  const weight = physical.reduce((s, i) => s + (i.weight_g || 0) * i.qty, 0);
  const checkRates = async () => {
    try {
      const r = await api.post("/shipping/rates", { country: ship.country, postal_code: ship.postal_code, weight_g: weight, value: cart.subtotal });
      setRates(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const applyCoupon = async () => {
    try { const r = await api.post("/coupons/validate", { code: coupon, subtotal: cart.subtotal }); setApplied(r.data); toast.success(`${r.data.code} ✓`); }
    catch (e) { setApplied(null); toast.error(errMsg(e)); }
  };
  const shipCost = physical.length && ship.method === "courier" ? rate?.price || 0 : 0;
  const couponDisc = applied?.discount || 0;
  const memberDisc = user ? Math.min(Math.floor((cart.subtotal * (config.member_discount_pct || 0)) / 100), cart.subtotal - couponDisc) : 0;
  const total = cart.subtotal - couponDisc - memberDisc + shipCost;
  const showForm = !!user || guest;

  const placeOrder = async () => {
    if (physical.length && ship.method === "courier" && !rate) { toast.error(t("check_rates")); return; }
    setBusy(true);
    try {
      const r = await api.post("/orders", {
        items: cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty, variant: i.variant || {}, session_id: i.session_id || null })),
        customer: cust, coupon: applied?.code || null,
        shipping: physical.length ? { ...ship, courier_code: rate?.courier_code || "", service_code: rate?.service_code || "" } : { method: "none" },
      });
      orderKey.set(r.data.order_id, r.data.access_key);
      placed.current = true;
      nav(`/order/${r.data.order_id}?pay=1`);
      cart.clear();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-12 grid lg:grid-cols-5 gap-12" data-testid="checkout-page">
      <div className="lg:col-span-3 space-y-12">
        <h1 className="font-serif text-4xl sm:text-5xl">{t("checkout")}</h1>
        {!user && <BuyChoice guest={guest} setGuest={setGuest} needsLogin={needsLogin} />}
        {user && memberDisc > 0 && <p className="text-sm text-[#2E6B40] border-l-2 border-[#2E6B40] pl-3" data-testid="member-discount-note">{t("discount")} member {config.member_discount_pct}% ✓</p>}
        {showForm && <>
        <Section title={t("contact")}>
          <input className={inputCls} placeholder="Full name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} data-testid="checkout-name-input" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={inputCls} type="email" placeholder="Email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} data-testid="checkout-email-input" />
            <input className={inputCls} placeholder="WhatsApp / phone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="checkout-phone-input" />
          </div>
        </Section>
        {physical.length > 0 && (
          <Section title={t("delivery")}>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["courier", Truck, t("courier")], ["pickup", Store, t("pickup")]].map(([m, Icon, label]) => (
                <button key={m} onClick={() => setShip({ ...ship, method: m })} data-testid={`delivery-${m}`}
                  className={`flex items-center gap-3 border p-4 text-sm text-left transition-colors ${ship.method === m ? "border-[#3B220C] bg-[#F6F2EE]" : "border-[#E6DFD5] bg-white"}`}><Icon size={18} />{label}</button>
              ))}
            </div>
            {ship.method === "pickup" && <p className="text-sm text-[#6E6259]" data-testid="pickup-address">{config.store_address}</p>}
            {ship.method === "courier" && (
              <div className="space-y-4">
                <Select value={ship.country} onValueChange={(v) => setShip({ ...ship, country: v })}>
                  <SelectTrigger className="rounded-none h-12 bg-white" data-testid="checkout-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <textarea className={inputCls} rows={2} placeholder="Street address" value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} data-testid="checkout-address-input" />
                <div className="grid grid-cols-2 gap-4">
                  <input className={inputCls} placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} data-testid="checkout-city-input" />
                  <input className={inputCls} placeholder="Postal code" value={ship.postal_code} onChange={(e) => setShip({ ...ship, postal_code: e.target.value })} data-testid="checkout-postal-input" />
                </div>
                <button onClick={checkRates} data-testid="check-rates-btn" className="border border-[#3B220C] px-5 py-3 text-sm hover:bg-[#F6F2EE] transition-colors">{t("check_rates")}</button>
                {rates && (
                  <div className="space-y-2" data-testid="shipping-rates">
                    {rates.simulated && <p className="text-[11px] font-mono text-[#C87D17]">DEMO RATES — courier API not connected yet</p>}
                    {rates.rates.map((r) => {
                      const id = `${r.courier_code}-${r.service_code}`;
                      return (
                        <button key={id} onClick={() => setRate(r)} data-testid={`rate-option-${id}`}
                          className={`w-full flex justify-between border p-4 text-sm transition-colors ${rate && `${rate.courier_code}-${rate.service_code}` === id ? "border-[#3B220C] bg-[#F6F2EE]" : "border-[#E6DFD5] bg-white"}`}>
                          <span>{r.courier_name} {r.service_name} <span className="text-[#6E6259]">· {r.duration}</span></span><span>{fmt(r.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Section>
        )}
        </>}
      </div>
      <aside className="lg:col-span-2 bg-white border border-[#E6DFD5] p-8 h-fit space-y-5 lg:sticky lg:top-32" data-testid="checkout-summary">
        {cart.items.map((i) => <div key={i.key} className="flex justify-between text-sm gap-4"><span>{i.name} ×{i.qty}</span><span>{fmt(i.price * i.qty)}</span></div>)}
        <div className="flex gap-2 pt-2">
          <input className={`${inputCls} py-2 uppercase`} placeholder={t("coupon")} value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
          <button onClick={applyCoupon} data-testid="coupon-apply-btn" className="border border-[#3B220C] px-4 text-sm">{t("apply")}</button>
        </div>
        <div className="border-t border-[#E6DFD5] pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>{t("subtotal")}</span><span>{fmt(cart.subtotal)}</span></div>
          {applied && <div className="flex justify-between text-[#2E6B40]"><span>{t("discount")} ({applied.code})</span><span data-testid="checkout-discount">−{fmt(applied.discount)}</span></div>}
          {memberDisc > 0 && <div className="flex justify-between text-[#2E6B40]"><span>Member ({config.member_discount_pct}%)</span><span data-testid="checkout-member-discount">−{fmt(memberDisc)}</span></div>}
          {physical.length > 0 && <div className="flex justify-between"><span>{t("shipping")}</span><span>{fmt(shipCost)}</span></div>}
          <div className="flex justify-between text-lg pt-2"><span>{t("total")}</span><span data-testid="checkout-total">{fmt(total)}</span></div>
          {config.usd_rate && <p className="text-[11px] text-[#6E6259]">Charged in IDR via Midtrans (QRIS, Virtual Account, E-Wallet, Card).</p>}
        </div>
        <button disabled={busy || needsLogin || !showForm} onClick={placeOrder} data-testid="place-order-btn"
          className="w-full bg-[#3B220C] text-white py-4 text-sm tracking-wider hover:bg-[#4A2C11] transition-colors disabled:opacity-40 flex justify-center items-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />}{t("place_order")}
        </button>
      </aside>
    </div>
  );
}
